import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, Plus, FileText, Mic, Sparkles, Calendar, Building2 } from "lucide-react";

const EMPTY_IMG =
  "https://images.pexels.com/photos/31107362/pexels-photo-31107362.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const StatBadge = ({ has, icon: Icon, label, testid }) => (
  <span
    data-testid={testid}
    className={`inline-flex items-center gap-1.5 px-2 py-1 text-[11px] font-medium border ${
      has ? "border-foreground/30 text-foreground" : "border-border text-muted-foreground/50"
    }`}
  >
    <Icon size={12} strokeWidth={2} />
    {label}
  </span>
);

export default function MeetingHistory() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [department, setDepartment] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [departments, setDepartments] = useState([]);

  const fetchMeetings = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (department !== "all") params.department = department;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/meetings", { params });
      setMeetings(data);
    } finally {
      setLoading(false);
    }
  }, [q, department, dateFrom, dateTo]);

  useEffect(() => {
    api.get("/departments").then((r) => setDepartments(r.data.departments));
  }, []);
  useEffect(() => {
    const t = setTimeout(fetchMeetings, 250);
    return () => clearTimeout(t);
  }, [fetchMeetings]);

  return (
    <Layout>
      <div className="p-8 lg:p-12 max-w-[1400px]">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-primary font-medium mb-2 mono">Repository</div>
            <h1 className="font-heading font-black text-4xl lg:text-5xl tracking-tight">Meeting History</h1>
            <p className="text-muted-foreground mt-2">Search and filter all recorded meetings across departments.</p>
          </div>
          <Button
            onClick={() => navigate("/new")}
            data-testid="new-meeting-btn"
            className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground h-11 px-6"
          >
            <Plus size={18} className="mr-2" /> New Meeting
          </Button>
        </div>

        {/* Filters */}
        <div className="border border-border bg-card p-4 mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="search-input"
              placeholder="Search by title…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="rounded-none pl-9 focus-visible:ring-2 focus-visible:ring-primary"
            />
          </div>
          {isAdmin && (
            <div>
              <Select value={department} onValueChange={setDepartment}>
                <SelectTrigger data-testid="filter-department" className="rounded-none">
                  <SelectValue placeholder="Department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Input
              type="date"
              data-testid="filter-date-from"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="rounded-none mono text-sm focus-visible:ring-2 focus-visible:ring-primary"
            />
          </div>
          <div>
            <Input
              type="date"
              data-testid="filter-date-to"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="rounded-none mono text-sm focus-visible:ring-2 focus-visible:ring-primary"
            />
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-muted-foreground py-20 text-center" data-testid="meetings-loading">Loading meetings…</div>
        ) : meetings.length === 0 ? (
          <div className="border border-border bg-card overflow-hidden" data-testid="empty-state">
            <div className="grid md:grid-cols-2">
              <div className="p-12 flex flex-col justify-center">
                <h3 className="font-heading font-black text-2xl tracking-tight mb-2">No meetings found</h3>
                <p className="text-muted-foreground mb-6">
                  {q || department !== "all" || dateFrom || dateTo
                    ? "Try adjusting your filters."
                    : "Create your first meeting to start uploading recordings and minutes."}
                </p>
                <Button
                  onClick={() => navigate("/new")}
                  data-testid="empty-new-meeting-btn"
                  className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground w-fit"
                >
                  <Plus size={18} className="mr-2" /> New Meeting
                </Button>
              </div>
              <div className="hidden md:block relative min-h-[280px] border-l border-border">
                <img src={EMPTY_IMG} alt="Meeting table" className="absolute inset-0 w-full h-full object-cover grayscale" />
              </div>
            </div>
          </div>
        ) : (
          <div className="border border-border divide-y divide-border bg-card" data-testid="meetings-list">
            {meetings.map((m) => (
              <button
                key={m.id}
                onClick={() => navigate(`/meetings/${m.id}`)}
                data-testid={`meeting-row-${m.id}`}
                className="w-full text-left px-5 py-4 flex items-center gap-5 hover:bg-muted/50 transition-colors duration-150 group"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-heading font-bold text-lg tracking-tight truncate group-hover:text-primary transition-colors duration-150">
                      {m.title}
                    </h3>
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <Building2 size={12} /> {m.department}
                    </span>
                  </div>
                  <div className="mono text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    <Calendar size={12} /> {m.date}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <StatBadge has={m.recordings?.length > 0} icon={Mic} label={`${m.recordings?.length || 0} Rec`} testid={`row-rec-${m.id}`} />
                  <StatBadge has={!!m.minutes_current} icon={FileText} label="Minutes" testid={`row-min-${m.id}`} />
                  <StatBadge has={!!m.summary} icon={Sparkles} label="AI" testid={`row-ai-${m.id}`} />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
